import React, { useState } from 'react';
import { 
    TrendingUp, TrendingDown, DollarSign, 
    ShoppingBag, Users, Receipt, Calendar, 
    ArrowRight, Activity
} from 'lucide-react';

const TrueROI = () => {
    const [timeframe, setTimeframe] = useState('monthly');

    // --- MOCK DATA (We will connect this to the backend in Step 2) ---
    const mockData = {
        daily: { sales: 2400, cogs: 650, labor: 400, opex: 150 },
        monthly: { sales: 72000, cogs: 19500, labor: 12000, opex: 4500 },
        yearly: { sales: 850000, cogs: 230000, labor: 144000, opex: 54000 }
    };

    const data = mockData[timeframe];
    
    // The Core Math Engine
    const totalCosts = data.cogs + data.labor + data.opex;
    const netProfit = data.sales - totalCosts;
    const profitMargin = data.sales > 0 ? ((netProfit / data.sales) * 100).toFixed(1) : 0;
    const isProfit = netProfit >= 0;

    // Helper for formatting currency
    const formatCurrency = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(val);

    return (
        <div className="min-h-screen bg-slate-950 font-sans text-slate-300 p-6 lg:p-8 selection:bg-blue-500/30">
            <div className="max-w-6xl mx-auto space-y-8">
                
                {/* --- 1. HEADER & TIME FILTER --- */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-slate-900/60 backdrop-blur-xl border border-slate-800 p-6 rounded-[2rem] shadow-2xl">
                    <div>
                        <h1 className="text-3xl font-black text-white tracking-tight flex items-center gap-3">
                            <div className="p-2 bg-blue-500/20 rounded-xl border border-blue-500/30">
                                <Activity className="text-blue-400" size={24} />
                            </div>
                            True Net Profit
                        </h1>
                        <p className="text-slate-400 mt-2 font-medium">Executive Financial Overview</p>
                    </div>

                    {/* Modern Segmented Control for Timeframe */}
                    <div className="flex bg-slate-950 p-1 rounded-xl border border-slate-800">
                        {['daily', 'monthly', 'yearly'].map((t) => (
                            <button
                                key={t}
                                onClick={() => setTimeframe(t)}
                                className={`px-6 py-2.5 rounded-lg text-sm font-bold capitalize transition-all duration-300 ${
                                    timeframe === t 
                                        ? 'bg-slate-800 text-white shadow-md' 
                                        : 'text-slate-500 hover:text-slate-300 hover:bg-slate-900'
                                }`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                </div>

                {/* --- 2. THE NORTH STAR (BIG METRIC) --- */}
                <div className={`relative overflow-hidden rounded-[2.5rem] border p-8 lg:p-12 shadow-2xl ${
                    isProfit 
                        ? 'bg-slate-900/80 border-emerald-500/20 shadow-emerald-900/10' 
                        : 'bg-slate-900/80 border-rose-500/20 shadow-rose-900/10'
                }`}>
                    {/* Ambient Background Glow */}
                    <div className={`absolute top-0 right-0 w-[500px] h-[500px] blur-[120px] rounded-full pointer-events-none opacity-10 ${
                        isProfit ? 'bg-emerald-500' : 'bg-rose-500'
                    }`}></div>

                    <div className="relative z-10 flex flex-col md:flex-row md:items-end justify-between gap-8">
                        <div>
                            <p className="text-sm font-bold uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-2">
                                <Calendar size={16} /> 
                                {timeframe} Net Profit
                            </p>
                            <h2 className="text-6xl lg:text-7xl font-black text-white tracking-tighter">
                                {formatCurrency(netProfit)}
                            </h2>
                        </div>
                        
                        <div className="flex items-center gap-4 bg-slate-950/50 p-4 rounded-2xl border border-slate-800">
                            <div className={`p-3 rounded-xl ${isProfit ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}`}>
                                {isProfit ? <TrendingUp size={28} /> : <TrendingDown size={28} />}
                            </div>
                            <div>
                                <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Profit Margin</p>
                                <p className={`text-2xl font-black ${isProfit ? 'text-emerald-400' : 'text-rose-400'}`}>
                                    {profitMargin}%
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* CSS Profit Waterfall Bar */}
                    <div className="mt-12">
                        <div className="flex justify-between text-xs font-bold text-slate-400 mb-3">
                            <span>Revenue: {formatCurrency(data.sales)}</span>
                            <span>Total Costs: {formatCurrency(totalCosts)}</span>
                        </div>
                        <div className="h-4 w-full bg-slate-950 rounded-full overflow-hidden flex shadow-inner">
                            {/* Costs takes up space from the left */}
                            <div className="h-full bg-slate-700 transition-all duration-1000" style={{ width: `${(totalCosts / data.sales) * 100}%` }}></div>
                            {/* Profit takes the rest */}
                            <div className={`h-full transition-all duration-1000 ${isProfit ? 'bg-emerald-500' : 'bg-rose-500'}`} style={{ flex: 1 }}></div>
                        </div>
                    </div>
                </div>

                {/* --- 3. THE 4 PILLARS (BREAKDOWN) --- */}
                <h3 className="text-xl font-black text-white pt-4">Financial Breakdown</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Pillar 1: Revenue */}
                    <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 p-6 rounded-3xl hover:border-blue-500/30 transition-colors">
                        <div className="w-12 h-12 rounded-2xl bg-blue-500/10 text-blue-400 flex items-center justify-center mb-4">
                            <DollarSign size={24} />
                        </div>
                        <p className="text-sm font-bold text-slate-400 mb-1">POS Sales</p>
                        <p className="text-2xl font-black text-white">{formatCurrency(data.sales)}</p>
                    </div>

                    {/* Pillar 2: COGS */}
                    <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 p-6 rounded-3xl hover:border-orange-500/30 transition-colors">
                        <div className="w-12 h-12 rounded-2xl bg-orange-500/10 text-orange-400 flex items-center justify-center mb-4">
                            <ShoppingBag size={24} />
                        </div>
                        <p className="text-sm font-bold text-slate-400 mb-1">Purchases (COGS)</p>
                        <p className="text-2xl font-black text-white">{formatCurrency(data.cogs)}</p>
                    </div>

                    {/* Pillar 3: Labor */}
                    <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 p-6 rounded-3xl hover:border-purple-500/30 transition-colors">
                        <div className="w-12 h-12 rounded-2xl bg-purple-500/10 text-purple-400 flex items-center justify-center mb-4">
                            <Users size={24} />
                        </div>
                        <p className="text-sm font-bold text-slate-400 mb-1">HR & Payroll</p>
                        <p className="text-2xl font-black text-white">{formatCurrency(data.labor)}</p>
                    </div>

                    {/* Pillar 4: OpEx */}
                    <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 p-6 rounded-3xl hover:border-pink-500/30 transition-colors">
                        <div className="w-12 h-12 rounded-2xl bg-pink-500/10 text-pink-400 flex items-center justify-center mb-4">
                            <Receipt size={24} />
                        </div>
                        <p className="text-sm font-bold text-slate-400 mb-1">Expense Ledger</p>
                        <p className="text-2xl font-black text-white">{formatCurrency(data.opex)}</p>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default TrueROI;